import React from "react";
import { Box, Button, Popover } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import dayjs from "dayjs";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { styled } from "@mui/system";

// Custom styled PickersDay to highlight selected and hovered days
const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) => prop !== "isSelected" && prop !== "isHovered" && prop !== "isOldData",
})(({ theme, isSelected, isHovered, day, isOldData }) => {
  // Determine week start/end based on whether date is in the past
  const weekStart = isOldData ? 0 : 1; // Sunday (0) for old, Monday (1) for new
  const weekEnd = isOldData ? 6 : 0;   // Saturday (6) for old, Sunday (0) for new
  
  return {
    borderRadius: 0,
    ...(isSelected && {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      "&:hover, &:focus": {
        backgroundColor: theme.palette.primary.main,
      },
    }),
    ...(isHovered && {
      backgroundColor: theme.palette.primary.light,
      "&:hover, &:focus": {
        backgroundColor: theme.palette.primary.light,
      },
    }),
    ...(day.day() === weekStart && {
      borderTopLeftRadius: "50%",
      borderBottomLeftRadius: "50%",
    }),
    ...(day.day() === weekEnd && {
      borderTopRightRadius: "50%",
      borderBottomRightRadius: "50%",
    }),
  };
});

function WeekPicker({ selectedDate, onDateChange, mb = 2 }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [hoveredDay, setHoveredDay] = React.useState(null);

  const isOpen = Boolean(anchorEl);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDaySelect = (date) => {
    onDateChange(date);
    handleClose();
  };

  // Check if two dates are in the same week (considering legacy format for dates before Oct 10, 2025)
  const isSameWeek = (date1, date2) => {
    const d1 = dayjs(date1);
    const d2 = dayjs(date2);
    const cutoffDate = dayjs('2025-10-12');
    
    // For dates before October 12, 2025, use Sunday-based week
    const isOldData = d1.isBefore(cutoffDate, 'day') || d2.isBefore(cutoffDate, 'day');
    
    if (isOldData) {
      // Calculate week start as Sunday for both dates
      const week1Start = d1.subtract(d1.day(), 'day').startOf('day');
      const week2Start = d2.subtract(d2.day(), 'day').startOf('day');
      return week1Start.isSame(week2Start, 'day');
    }
    
    // For October 12, 2025 and after, use Monday-based week
    return d1.isSame(d2, 'week');
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box display="flex" justifyContent="flex-end" mb={mb}>
        <Button
          variant="contained"
          onClick={isOpen ? handleClose : handleOpen}
          aria-describedby={isOpen ? "week-picker-popover" : undefined}
        >
          {isOpen ? "Close Calendar" : "Open Calendar"}
        </Button>
      </Box>
      <Popover
        id="week-picker-popover"
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1.5,
            boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px",
            borderRadius: 1,
          },
        }}
      >
        <Box sx={{ p: 0 }}>
          <DateCalendar
            sx={{
              ".MuiDayCalendar-weekDayLabel": {
                color: "black",
              },
            }}
            value={selectedDate}
            onChange={handleDaySelect}
            showDaysOutsideCurrentMonth
            displayWeekNumber
            slots={{
              day: (props) => {
                const cutoffDate = dayjs('2025-10-12');
                const isOldData = dayjs(props.day).isBefore(cutoffDate, 'day');
                return (
                  <CustomPickersDay
                    {...props}
                    sx={{ px: 2.5 }}
                    isOldData={isOldData}
                    isSelected={isSameWeek(props.day, selectedDate)}
                    isHovered={hoveredDay && isSameWeek(props.day, hoveredDay)}
                    onPointerEnter={() => setHoveredDay(props.day)}
                    onPointerLeave={() => setHoveredDay(null)}
                  />
                );
              },
            }}
          />
        </Box>
      </Popover>
    </LocalizationProvider>
  );
}

export default WeekPicker;
